import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  LinearProgress,
  ThemeProvider,
  createTheme,
  Grid,
  Divider,
  CircularProgress
} from '@mui/material';
import QuizApi from '../apis/quiz';
import TurnstileWidget from '../Components/TurnstileWidget';
import BouncingCTA from '../Components/BouncingButton/BouncingCTA';
import { Link } from 'react-router-dom';

// Custom theme to match the design
const theme = createTheme({
  palette: {
    primary: {
      main: '#4361ee',
    },
    secondary: {
      main: '#4caf50',
    },
    background: {
      default: '#1e2d58',
      paper: '#ffffff',
    },
    error: {
      main: '#f44336',
    }
  },
  typography: {
    h2: {
      fontWeight: 600,
      color: '#ffffff',
    },
    subtitle1: {
      color: '#ffffff',
      marginBottom: '2rem',
    },
    h5: {
      fontWeight: 600,
      textAlign: 'center',
      marginBottom: '1.5rem',
    }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 24,
          padding: '12px 24px',
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          padding: '2rem',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          margin: '8px 0',
          width: '100%',
          border: '1px solid #e0e0e0',
          borderRadius: 16,
          padding: '12px 16px',
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: '4px',
          position: 'absolute',
          left: '15px',
        }
      }
    }
  },
});

const Quiz = () => {
  const [selectedOption, setSelectedOption] = useState('');
  const [quizData, setQuizData] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [questionOptions, setQuestionOptions] = useState([]);
  const [isLoadingQuiz, setIsLoadingQuiz] = useState(true);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  const [quizResults, setQuizResults] = useState({
    correctAnswers: 0,
    incorrectAnswers: 0,
    suggestionIndex: 0
  });

  // Handle verification completion from TurnstileWidget
  const handleVerify = (token) => {
    setIsVerified(true);

    // Once verified, load the quiz
    fetchQuiz(token);
  };

  // Function to fetch quiz after verification
  const fetchQuiz = (verificationToken) => {
    setIsLoadingQuiz(true);
    // You may want to pass the verification token to your API
    QuizApi.getQuiz(verificationToken).then((response) => {
      const sortedQuestions = [...response.data].sort((a, b) => a.question_id - b.question_id);
      setQuizData(sortedQuestions);
      setCurrentQuestionIndex(0);
      setIsLoadingQuiz(false);

      // Load options for the first question
      if (sortedQuestions.length > 0) {
        fetchQuestionOptions(sortedQuestions[0].question_id);
      }
    }).catch(error => {
      console.error('Error fetching quiz:', error);
      setIsLoadingQuiz(false);
    });
  };

  // Fetch options for a specific question
  const fetchQuestionOptions = (questionId) => {
    setIsLoadingOptions(true);
    setQuestionOptions([]); // Clear previous options while loading

    QuizApi.getSelections(questionId).then((response) => {
      setQuestionOptions(response.data);
      setIsLoadingOptions(false);

      // Check if user has already answered this question
      const existingAnswer = userAnswers.find(answer => answer.question_id === questionId);
      if (existingAnswer) {
        setSelectedOption(existingAnswer.user_answer);
      } else {
        setSelectedOption('');
      }
    }).catch(error => {
      console.error('Error fetching options:', error);
      setQuestionOptions([]);
      setIsLoadingOptions(false);
    });
  };

  const handleOptionChange = (event) => {
    setSelectedOption(Number(event.target.value));
  };

  const handleNext = () => {
    if (quizData.length === 0) return;

    // Save the current answer
    const currentQuestionId = quizData[currentQuestionIndex].question_id;
    const updatedAnswers = [...userAnswers];

    // Find the index of the existing answer, if any
    const existingAnswerIndex = updatedAnswers.findIndex(ans => ans.question_id === currentQuestionId);

    const answerObject = {
      question_id: currentQuestionId,
      user_answer: selectedOption
    };

    if (existingAnswerIndex >= 0) {
      // Update existing answer
      updatedAnswers[existingAnswerIndex] = answerObject;
    } else {
      // Add new answer
      updatedAnswers.push(answerObject);
    }

    setUserAnswers(updatedAnswers);

    // Check if it's the last question
    if (currentQuestionIndex < quizData.length - 1) {
      // Move to next question
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);

      // Fetch options for the next question
      fetchQuestionOptions(quizData[nextIndex].question_id);
    } else {
      // Quiz completed
      setQuizCompleted(true);

      const getLargestValueIndex = (response) => {
        // Create an array of values in the specific order
        const values = [
          response.bias,        // index 0
          response.fact,          // index 1
          response.misinformation // index 2
        ];

        // Find the index of the largest value
        let largestIndex = 0;
        let largestValue = values[0];

        for (let i = 1; i < values.length; i++) {
          if (values[i] > largestValue) {
            largestValue = values[i];
            largestIndex = i;
          }
        }

        return largestIndex;
      }

      // Submit answers to API
      QuizApi.submitQuiz(updatedAnswers, quizData[0]["quiz_id"]).then((response) => {
        if (response) {
          const index = getLargestValueIndex(response);
          const totalQuestions = quizData.length;
          const correct = response.correct_answers || 0;
          const scorePercent = totalQuestions > 0 ? Math.round((correct / totalQuestions) * 100) : 0;

          setQuizResults({
            correctAnswers: correct,
            incorrectAnswers: totalQuestions - correct,
            suggestionIndex: index,
            scorePercentage: scorePercent,
          });
          localStorage.setItem("latest_score_percent", scorePercent.toString());
        } else {
          // Simulate results if API doesn't return them
          // In a real app, you'd get this from the API response
          setQuizResults({
            correctAnswers: response.correct_answers || 0,
            incorrectAnswers: response.all_answers - response.correct_answers || 0
          });
        }
      }).catch(error => {
        console.error('Error submitting quiz:', error);
        // Simulate results if API fails
        setQuizResults({
          correctAnswers: 0,
          incorrectAnswers: 0
        });
      });
    }
  };

  // Handle navigation to dashboard
  const handleGotoDashboard = () => {
    // Navigate to dashboard page
    // Replace this with your actual navigation method (e.g., React Router)
    window.location.href = '/personal-dashboard';
    // If using React Router, you'd use:
    // history.push('/dashboard');
    // or
    // navigate('/dashboard');
  };

  // Calculate progress percentage
  const calculateProgress = () => {
    if (quizData.length === 0) return 0;
    return ((currentQuestionIndex) / quizData.length) * 100;
  };

  // Result screen to show when quiz is completed
  const renderQuizResult = () => {
    const totalQuestions = quizData.length;
    const scorePercentage = totalQuestions > 0
      ? Math.round((quizResults.correctAnswers / totalQuestions) * 100)
      : 0;

    // Misinformation tips data
    const misinformationTips = [
      {
        category: "Fact (in relation to misinformation)",
        suggestions: [
          "Promote Media Literacy: Teach people how to fact-check information using credible sources and critical thinking.",
          "Use Fact-Checking Tools: Encourage the use of platforms like Snopes, FactCheck.org, or browser extensions that flag misleading content.",
          "Highlight Source Transparency: Push for clear sourcing in media so audiences can trace information back to its origin."
        ],
        course: "/course/1",
        courseTitle: "Fact-Checking & Source Verification",
      },
      {
        category: "Bias",
        suggestions: [
          "Promote Diverse Perspectives: Share content from a wide range of cultural, political, and social viewpoints to reduce echo chambers.",
          "Educate on Media Literacy: Encourage critical thinking by teaching users how to identify bias in headlines, language, and source selection.",
          "Use Transparent Algorithms: Advocate for platforms to disclose how content is curated and whether personalization may reinforce existing biases."
        ],
        course: "/course/2",
        courseTitle: "The Psychology of Belief",
      },
      {
        category: "Misinformation (combating it directly)",
        suggestions: [
          "Strengthen Platform Policies: Advocate for social media platforms to flag or reduce visibility of false content.",
          "Encourage Reporting of False Info: Make it easy for users to report misleading content and reward engagement with verified info.",
          "Build Community Fact-Checkers: Create networks of local or peer-led fact-checking groups to keep info in check at the grassroots level."
        ],
        course: "/course/3",
        courseTitle: "Understanding Misinformation",
      }
    ];

    return (
      <Paper elevation={3} sx={{ maxWidth: 450, mx: 'auto', mt: 4, p: 3 }}>
        <Typography variant="h5" align="center" gutterBottom>
          Quiz Completed!
        </Typography>
        <Typography variant="body1" align="center" sx={{ mb: 3 }}>
          Thank you for completing the quiz. Your responses have been submitted.
        </Typography>

        {/* Results Display */}
        <Box sx={{
          bgcolor: '#f5f5f5',
          p: 3,
          borderRadius: 4,
          mb: 3
        }}>
          <Typography variant="h6" align="center" gutterBottom>
            Your Results
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Typography variant="h4" sx={{ color: scorePercentage >= 70 ? '#4caf50' : '#f44336' }}>
              {scorePercentage}%
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                color: 'secondary.main'
              }}>
                <Typography variant="h6" color="secondary">
                  {quizResults.correctAnswers}
                </Typography>
                <Typography variant="body2">
                  Correct
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                color: 'error.main'
              }}>
                <Typography variant="h6" color="error">
                  {quizResults.incorrectAnswers}
                </Typography>
                <Typography variant="body2">
                  Incorrect
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Random Misinformation Category */}
        <Box sx={{
          bgcolor: '#e3f2fd',
          p: 2,
          borderRadius: 2,
          mb: 3,
          border: '1px solid rgb(0, 0, 0)'
        }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: 'text.primary' }}>
            Fighting Misinformation Focus:
          </Typography>
          <Typography variant="body1">
            {misinformationTips[quizResults.suggestionIndex].category}
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
            {misinformationTips[quizResults.suggestionIndex].suggestions.map((suggestion, index) => (
              <li key={index}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {suggestion}
                </Typography>
              </li>
            ))}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: 'text.primary' }}>
                Suggested Course:
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                We suggest you visit the course: <strong>{misinformationTips[quizResults.suggestionIndex].courseTitle}</strong>
              </Typography>
              <Button
                variant="contained"
                component={Link}

                to={misinformationTips[quizResults.suggestionIndex].course}
                sx={{
                  backgroundColor: '#11ab73',
                  textTransform: 'none',
                  fontWeight: 'bold',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: '#074014',
                    boxShadow: 'none',
                  },
                }}
              >
                Go to Course
              </Button>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleGotoDashboard}
            sx={{ minWidth: 180 }}
          >
            Go to Dashboard
          </Button>

          <Button
            variant="outlined"
            color="primary"
            size="large"
            onClick={() => window.location.reload()}
            sx={{ minWidth: 180 }}
          >
            Start Again
          </Button>
        </Box>
      </Paper>
    );
  };

  // Render verification screen before loading the quiz
  const renderVerification = () => {
    return (
      <Paper elevation={3} sx={{ maxWidth: 450, mx: 'auto', mt: 4, textAlign: 'center', py: 4 }}>
        <Typography variant="h6" gutterBottom>
          Verify to Continue
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Please complete the verification below to access the quiz
        </Typography>

        <TurnstileWidget onVerify={handleVerify} />
      </Paper>
    );
  };

  // Loading screen for the entire quiz
  const renderLoadingQuiz = () => {
    return (
      <Paper elevation={3} sx={{ maxWidth: 450, mx: 'auto', mt: 4, textAlign: 'center', py: 8 }}>
        <CircularProgress size={60} thickness={4} sx={{ mb: 3 }} />
        <Typography variant="h6">
          Loading Quiz...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please wait while we prepare your quiz questions
        </Typography>
      </Paper>
    );
  };

  // Current question screen
  const renderQuizQuestion = () => {
    if (quizData.length === 0) {
      return <Typography>Loading questions...</Typography>;
    }

    const currentQuestion = quizData[currentQuestionIndex];

    return (
      <Paper elevation={3} sx={{ maxWidth: 450, mx: 'auto', mt: 4 }}>
        <LinearProgress
          variant="determinate"
          value={calculateProgress()}
          sx={{
            height: 8,
            borderRadius: 4,
            mb: 4,
            bgcolor: '#e0e0e0',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
            }
          }}
        />

        <Typography variant="h5">
          {currentQuestion.content}
        </Typography>

        <FormControl component="fieldset" sx={{ width: '100%', mt: 3 }}>
          {isLoadingOptions ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Loading answer options...
              </Typography>
            </Box>
          ) : (
            <RadioGroup
              aria-label="quiz-option"
              name="quiz-option"
              value={selectedOption}
              onChange={handleOptionChange}
            >
              {questionOptions.length > 0 ? (
                questionOptions.map((option) => (
                  <FormControlLabel
                    key={option.order}
                    value={option.order}
                    control={<Radio color="primary" />}
                    label={option.content}
                    sx={{
                      bgcolor: selectedOption === option.order ? '#f0f7ff' : 'transparent',
                      border: selectedOption === option.order ? '1px solid #4361ee' : '1px solid #e0e0e0',
                      position: 'relative',
                      pl: 7,
                      width: '80%',
                    }}
                  />
                ))
              ) : (
                <Typography variant="body2" sx={{ my: 2, textAlign: 'center', color: 'text.secondary' }}>
                  No options available for this question.
                </Typography>
              )}
            </RadioGroup>
          )}
        </FormControl>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            onClick={handleNext}
            disabled={selectedOption === '' || isLoadingOptions}
            sx={{
              minWidth: 180,
              bgcolor: '#4caf50',
              '&:hover': {
                bgcolor: '#43a047',
              }
            }}
          >
            {currentQuestionIndex < quizData.length - 1 ? 'Next' : 'Submit'}
          </Button>
        </Box>
      </Paper>
    );
  };

  // Determine what to display based on verification and loading status
  const renderContent = () => {
    if (!isVerified) {
      return renderVerification();
    } else if (isLoadingQuiz) {
      return renderLoadingQuiz();
    } else if (quizCompleted) {
      return renderQuizResult();
    } else {
      return renderQuizQuestion();
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          // bgcolor: 'background.default',
          backgroundImage: 'linear-gradient(to bottom right,rgba(7, 34, 109, 0.67),rgba(84, 105, 7, 0.54))',
          minHeight: '100vh',
          py: 6,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h2" align="center" gutterBottom>
            How safe are you to misinformation?
          </Typography>
          <Typography variant="subtitle1" align="center">
            Take this quick quiz to discover your digital habits and get personalized tips to stay sharp online!
          </Typography>

          {renderContent()}
        </Container>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'stretch',
            gap: 4, // spacing between cards
            flexWrap: 'wrap', // allow wrapping on smaller screens
            mt: 4, // top margin
          }}
        >
          <BouncingCTA
            text="Unsure about a piece of content? Paste it here to verify its credibility."
            link="/quick-check"
            linkText="Analyze Now"
            bgImage="/images/navcards/cred_checker.png"
          />
          <BouncingCTA
            text="Dive into the game and put your knowledge to the test and Defeat ECHO!"
            link="/defence-game"
            linkText="Play the Game"
            bgImage="/images/navcards/gameplay.png"
          />

        </Box>
        {/* <BouncingCTA
          text="Your central hub for all things related to building digital trust!"
          link="/dashboard"
          linkText="Go to Your Dashboard"
          bgImage="/images/navcards/dashboard.png"
        /> */}
      </Box>
    </ThemeProvider>
  );
};

export default Quiz;